export const DEFAULT_LEGACY_ORDER_AUTO_DONE_CUTOFF_ISO = "2026-02-01T00:00:00Z";

type DateLike =
  | Date
  | string
  | number
  | null
  | undefined
  | {
    toDate?: () => Date;
  };

export type LegacyOrderDateFields = {
  manual?: boolean;
  submittedAt?: DateLike;
  approvedAt?: DateLike;
  created?: DateLike;
  nav_date?: DateLike;
  expected_nav_date?: DateLike;
  fund?: { type?: string };
  burgan_cutoff_date?: DateLike;
};

const KUWAIT_UTC_OFFSET_MS = 3 * 60 * 60 * 1000;

// NAV dates and cutoffs are saved at different hours (00:00 UTC, 03:00 UTC, Kuwait midnight),
// so compare them as Kuwait calendar days.
const toKuwaitDay = (date: Date) => new Date(date.getTime() + KUWAIT_UTC_OFFSET_MS).toISOString().slice(0, 10);

export function toJsDate(value?: DateLike): Date | undefined {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  if (typeof value === "object" && typeof value.toDate === "function") {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : undefined;
  }

  return undefined;
}

export function getLegacyOrderReferenceDate(order: LegacyOrderDateFields): Date | undefined {
  const submittedAt = toJsDate(order.submittedAt);
  const approvedAt = toJsDate(order.approvedAt);
  const createdAt = toJsDate(order.created);

  if (order.manual) {
    return submittedAt ?? approvedAt ?? createdAt;
  }

  return submittedAt ?? createdAt ?? approvedAt;
}

export function getLegacyOrderAutoDoneCutoffDate(
  order: LegacyOrderDateFields,
  fallbackCutoff: DateLike = DEFAULT_LEGACY_ORDER_AUTO_DONE_CUTOFF_ISO,
): Date | undefined {
  return toJsDate(order.burgan_cutoff_date) ?? toJsDate(fallbackCutoff);
}

export function isLegacyOrderAutoDone(
  order: LegacyOrderDateFields,
  fallbackCutoff: DateLike = DEFAULT_LEGACY_ORDER_AUTO_DONE_CUTOFF_ISO,
): boolean {
  // Open-ended funds with their own Burgan cutoff are judged by the NAV the order belongs to.
  // Everything else (closed-ended, default cutoff, no NAV date yet) keeps the submitted-date rule.
  const fundCutoffDate = toJsDate(order.burgan_cutoff_date);
  const navDate = toJsDate(order.nav_date) ?? toJsDate(order.expected_nav_date);
  if (order.fund?.type === "open_ended" && fundCutoffDate && navDate) {
    return toKuwaitDay(navDate) < toKuwaitDay(fundCutoffDate);
  }

  const referenceDate = getLegacyOrderReferenceDate(order);
  const cutoffDate = getLegacyOrderAutoDoneCutoffDate(order, fallbackCutoff);
  return !!referenceDate && !!cutoffDate && referenceDate.getTime() < cutoffDate.getTime();
}
